import React, { useState, lazy } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantHeader } from '../components/restaurant/RestaurantHeader';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData, useMarketplaceData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, Utensils, ChevronRight, Plus } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
import { formatCurrency } from '../lib/utils';
// Mexican-inspired gradient colors for category cards
const MEXICAN_GRADIENTS = ['linear-gradient(135deg, #006847 0%, #2AABB3 100%)', 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)', 'linear-gradient(135deg, #E8751A 0%, #F4D35E 100%)', 'linear-gradient(135deg, #6B2FA0 0%, #CE1126 100%)', 'linear-gradient(135deg, #2AABB3 0%, #006847 100%)', 'linear-gradient(135deg, #C9A34E 0%, #E8751A 100%)', 'linear-gradient(135deg, #CE1126 0%, #6B2FA0 100%)', 'linear-gradient(135deg, #006847 0%, #C9A34E 100%)'];
export function RestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    settings,
    loading: restaurantLoading,
    error
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    zones
  } = useMarketplaceData();
  const {
    addItem
  } = useCart();
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  // Loading state — dark
  if (restaurantLoading) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-1506">
        <MarketplaceHeader data-id="element-1507" />
        <div className="h-48 sm:h-64 w-full bg-[#141414] animate-pulse" data-id="element-1508" />
        <div className="max-w-5xl mx-auto px-4 w-full mt-8" data-id="element-1509">
          <div className="h-10 w-1/3 mb-4 bg-[#1a1a1a] rounded-lg animate-pulse" data-id="element-1510" />
          <div className="h-4 w-2/3 mb-8 bg-[#1a1a1a] rounded animate-pulse" data-id="element-1511" />
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3" data-id="element-1512">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-40 rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-1513" />)}
          </div>
        </div>
      </div>;
  }
  // Error state — dark
  if (error || !restaurant) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-1514">
        <MarketplaceHeader data-id="element-1515" />
        <div className="flex-1 flex items-center justify-center" data-id="element-1516">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible en este momento." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-1517" />
        </div>
        <Footer data-id="element-1518" />
      </div>;
  }
  const zoneName = zones.find(z => z.id === restaurant.zone_id)?.name;
  const highlightedItems = items.slice(0, 4);
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen flex flex-col bg-[#0B0B0B] pb-24" data-id="element-1519">
      <MarketplaceHeader data-id="element-1520" />

      <main className="flex-1" data-id="element-1521">
        <RestaurantHeader restaurant={restaurant} settings={settings} zoneName={zoneName} data-id="element-1522" />

        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-1523">
          {/* Highlighted Items — compact horizontal rows */}
          {highlightedItems.length > 0 && <section className="mb-8 sm:mb-12" data-id="element-1524">
              <div className="flex items-center justify-between mb-3 sm:mb-4" data-id="element-1525">
                <h2 className="text-xl sm:text-2xl font-black text-white" data-id="element-1526">
                  ⭐ Destacados
                </h2>
              </div>
              <div className="flex flex-col gap-2" data-id="element-1527">
                {highlightedItems.map(item => <div key={item.id} onClick={() => handleProductClick(item)} className="flex items-center gap-3 bg-[#141414] border border-white/5 rounded-xl p-2 cursor-pointer active:opacity-80 transition-opacity" data-id="element-1528">
                    <img src={item.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop&q=60'} alt={item.name} className="w-16 h-16 rounded-lg object-cover shrink-0" loading="lazy" data-id="element-1529" />
                    <div className="flex-1 min-w-0" data-id="element-1530">
                      <h3 className="font-bold text-white text-sm leading-tight line-clamp-1" data-id="element-1531">
                        {item.name}
                      </h3>
                      {item.description && <p className="text-[11px] text-white/40 line-clamp-1 mt-0.5" data-id="element-1532">
                          {item.description}
                        </p>}
                      <span className="font-bold text-[#F4D35E] text-sm mt-0.5 block" data-id="element-1533">
                        {formatCurrency(item.price)}
                      </span>
                    </div>
                    <button className="w-8 h-8 rounded-full flex items-center justify-center bg-[#006847] text-white shrink-0 active:scale-90 transition-transform" onClick={e => {
                e.stopPropagation();
                handleProductClick(item);
              }} data-id="element-1534">
                      <Plus className="w-4 h-4" data-id="element-1535" />
                    </button>
                  </div>)}
              </div>
            </section>}

          {/* Categories Quick Nav — dark cards with Mexican gradients */}
          <section className="mb-8 sm:mb-12" data-id="element-1536">
            <h2 className="text-xl sm:text-2xl font-black text-white mb-4 sm:mb-6" data-id="element-1537">
              🔥 Categorías
            </h2>
            {menuLoading ? <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-id="element-1538">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-24 rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-1539" />)}
              </div> : categories.length > 0 ? <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-id="element-1540">
                {categories.map((category, idx) => <Link key={category.id} to={`/r/${restaurant.slug}/menu#${category.id}`} className="relative overflow-hidden p-4 sm:p-5 rounded-2xl text-center group transition-transform active:scale-95" style={{
              background: MEXICAN_GRADIENTS[idx % MEXICAN_GRADIENTS.length]
            }} data-id="element-1541">
                    {/* Subtle overlay for depth */}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" data-id="element-1542" />
                    <div className="relative z-10" data-id="element-1543">
                      <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 bg-white/20" data-id="element-1544">
                        <Utensils className="w-5 h-5 text-white" data-id="element-1545" />
                      </div>
                      <h3 className="font-bold text-white text-xs sm:text-sm leading-tight" data-id="element-1546">
                        {category.name}
                      </h3>
                    </div>
                  </Link>)}
              </div> : <p className="text-white/40" data-id="element-1547">No hay categorías disponibles.</p>}
          </section>

          {/* CTA Full Menu — dark themed */}
          <div className="text-center p-6 sm:p-8 rounded-2xl border border-white/10 relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, #006847 0%, #0B0B0B 50%, #CE1126 100%)'
        }} data-id="element-1548">
            <div className="absolute inset-0 bg-black/60" data-id="element-1549" />
            <div className="relative z-10" data-id="element-1550">
              <h3 className="text-lg sm:text-xl font-black text-white mb-2" data-id="element-1551">
                ¿Listo para ordenar? 🌮
              </h3>
              <p className="text-white/60 text-sm mb-1" data-id="element-1552">
                Explora el menú completo de{' '}
                <span className="font-bold text-white" data-id="element-1553">{restaurant.name}</span>{' '}
                y personaliza tu pedido.
              </p>
              <p className="text-xs text-white/30 mb-5" data-id="element-1554">
                Tu pedido irá directamente a este restaurante por WhatsApp.
              </p>
              <button onClick={() => navigate(`/r/${restaurant.slug}/menu`)} className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-[#0B0B0B] transition-colors active:scale-95" style={{
              background: `linear-gradient(135deg, ${primaryColor}, #C9A34E)`
            }} data-id="element-1555">
                Ver Menú Completo <ChevronRight className="w-5 h-5" data-id="element-1556" />
              </button>
            </div>
          </div>
        </div>
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-1557" />

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-1558" />

      <Footer data-id="element-1559" />
    </div>;
}